import React, { useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Alert,
  CircularProgress,
  Button,
  IconButton,
  Slide,
} from "@mui/material";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EmailIcon from "@mui/icons-material/Email";
import ReactMarkdown from "react-markdown";
import type {
  EmailAutomationSectionEnriched,
  EmailAutomationSectionObjectEnriched,
} from "@services/emailAutomationPipeline/types";
import {
  EventsTimeline,
  type TimelineEvent,
} from "../../../eventsTimelineComponent";
import { EmailPreview } from "../shipmentTimelineAndAutomation/EmailPreview";

interface EmailAutomationPresentationComponentProps {
  sections: EmailAutomationSectionEnriched[];
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}

/**
 * Check if an object is an enriched TimelineEvent (has 'name' field from server enrichment)
 */
function isEnrichedTimelineEvent(
  obj: EmailAutomationSectionObjectEnriched,
): boolean {
  return (
    obj.type === "email" &&
    "name" in obj &&
    typeof (obj as Record<string, unknown>).name === "string"
  );
}

/**
 * Convert enriched section objects to TimelineEvent array for EventsTimeline
 */
function convertToTimelineEvents(
  objects: EmailAutomationSectionObjectEnriched[],
): TimelineEvent[] {
  return objects.filter(isEnrichedTimelineEvent).map((obj) => {
    // Convert to TimelineEvent format
    const enriched = obj as unknown as TimelineEvent;
    return {
      name: enriched.name,
      attachments: enriched.attachments || [],
      emailId: enriched.emailId,
      emailSubject: enriched.emailSubject,
      emailFrom: enriched.emailFrom,
      emailTo: enriched.emailTo,
      date: enriched.date,
      threadId: enriched.threadId,
      snippet: enriched.snippet,
      preview: enriched.preview,
      fromOwnDomain: enriched.fromOwnDomain,
      categories: enriched.categories,
    };
  });
}

export const EmailAutomationPresentationComponent: React.FC<
  EmailAutomationPresentationComponentProps
> = ({
  sections,
  loading = false,
  error = null,
  emptyMessage = "No data found.",
}) => {
  const [selectedEmailId, setSelectedEmailId] = useState<string | null>(null);
  console.log(selectedEmailId);
  const [timelineEvents, setTimelineEvents] = useState<TimelineEvent[] | null>(
    null,
  );

  const handleOpenEmail = (emailId: string) => {
    setSelectedEmailId(emailId);
  };

  const handleCloseEmail = () => {
    setSelectedEmailId(null);
  };

  const handleOpenTimeline = (events: TimelineEvent[]) => {
    setTimelineEvents(events);
  };

  const handleCloseTimeline = () => {
    setTimelineEvents(null);
  };

  // Loading state
  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="200px"
      >
        <CircularProgress size={24} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Loading presentation...
        </Typography>
      </Box>
    );
  }

  // Error state
  if (error) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  // Empty state
  if (sections.length === 0) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="info">{emptyMessage}</Alert>
      </Box>
    );
  }

  // Render sections
  return (
    <Box sx={{ p: 2 }}>
      {sections.map((section, index) => (
        <Paper
          key={index}
          elevation={1}
          sx={{
            mb: 2,
            p: 2,
            "&:last-child": { mb: 0 },
            overflow: "hidden",
          }}
        >
          {section.title && (
            <Typography
              variant="subtitle1"
              fontWeight="bold"
              gutterBottom
              sx={{ color: "primary.main" }}
            >
              {section.title}
            </Typography>
          )}
          <Box
            sx={{
              overflow: "hidden",
              "& p": { my: 1 },
              "& ul, & ol": { pl: 2, my: 1 },
              "& li": { mb: 0.5 },
              "& a": {
                color: "primary.main",
                textDecoration: "none",
                "&:hover": { textDecoration: "underline" },
              },
              "& strong": { fontWeight: 600 },
              "& code": {
                bgcolor: "grey.100",
                px: 0.5,
                py: 0.25,
                borderRadius: 0.5,
                fontSize: "0.875em",
                wordBreak: "break-word",
              },
              "& pre": {
                overflow: "auto",
                maxWidth: "100%",
              },
            }}
          >
            <ReactMarkdown>{section.content}</ReactMarkdown>
          </Box>

          {/* Render related emails button if present */}
          {section.objects &&
            section.objects.length > 0 &&
            (() => {
              const emailEvents = convertToTimelineEvents(section.objects);
              if (emailEvents.length === 0) return null;

              return (
                <Box sx={{ mt: 2 }}>
                  <Button
                    variant="outlined"
                    fullWidth
                    startIcon={<EmailIcon />}
                    endIcon={<ArrowForwardIcon />}
                    onClick={() => handleOpenTimeline(emailEvents)}
                    sx={{
                      justifyContent: "space-between",
                      textTransform: "none",
                      py: 1.5,
                      fontWeight: 600,
                      borderColor: "primary.main",
                      borderWidth: 2,
                      color: "primary.main",
                      "&:hover": {
                        borderColor: "primary.dark",
                        borderWidth: 2,
                        backgroundColor: "primary.50",
                      },
                    }}
                  >
                    See related emails ({emailEvents.length})
                  </Button>
                </Box>
              );
            })()}
        </Paper>
      ))}

      {/* Full-view Timeline Panel */}
      <Slide
        direction="left"
        in={timelineEvents !== null}
        mountOnEnter
        unmountOnExit
      >
        <Box
          sx={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "background.paper",
            zIndex: 1300,
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Black header */}
          <Box
            sx={{
              backgroundColor: "#000",
              color: "#fff",
              display: "flex",
              alignItems: "center",
              px: 1,
              py: 1,
              flexShrink: 0,
            }}
          >
            <IconButton
              onClick={handleCloseTimeline}
              sx={{ color: "#fff", mr: 1 }}
              size="small"
            >
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="subtitle1" fontWeight={600}>
              Emails Timeline
            </Typography>
          </Box>

          {/* Timeline content */}
          <Box sx={{ flex: 1, minHeight: 0, overflow: "hidden" }}>
            {timelineEvents && (
              <EventsTimeline
                dateColumnWidth={0}
                events={timelineEvents}
                onOpenEmail={handleOpenEmail}
                showFilters={false}
                showStickyHeaders={true}
                numberOfLanes={2}
                topPadding={10}
                bottomPadding={20}
                bubbleWidth={70}
                laneSpacing={20}
                datePosition="center"
              />
            )}
          </Box>
        </Box>
      </Slide>

      {/* Email Preview - rendered after Slide so it stacks on top (same z-index) */}
      {selectedEmailId && (
        <EmailPreview
          mode="readonly"
          emailId={selectedEmailId}
          onDismiss={handleCloseEmail}
        />
      )}
    </Box>
  );
};
